import { useParams } from 'react-router-dom';
import { getReservation } from '@/lib/api';
import { useApiGet } from '@/lib/api/client';
import Loading from '@/components/loading';
import '../print.css';
import { ASSETS_URL } from '@/consts';
import Markdown from 'react-markdown';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import {
  calculateBalance,
  calculateDeposit,
  calculateTotal,
  sortedVariantsByReservation,
} from '@/lib/reservation';
import {
  formatDate,
  formatDateTime,
  getPropertyValueOrReference,
} from '@/lib/utils';
import { Button } from '@/ui/button';
import {
  Printer,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Building2,
  CreditCard,
} from 'lucide-react';

function Ticket() {
  const { id } = useParams();
  const { t } = useTranslation();

  const { data } = useApiGet([], async () => await getReservation({ id }), {
    enabled: true,
    refetchOnWindowFocus: true,
    retry: 0,
  });

  if (!data) {
    return <Loading />;
  }

  const ticketCode = data?.code?.slice(0, 6) ?? data?.id?.slice(0, 6);
  const showPayment =
    data.payment_status && data.payment_status !== 'none';
  const showSeller = data.service.supplier.id !== data.seller.id;

  return (
    data && (
      <div className="relative h-full print">
        <Button
          className="fixed top-2 right-2 z-30 print:hidden"
          onClick={() => window.print()}
        >
          <Printer className="mr-1 w-4 h-4" />
          {t('ticket.print')}
        </Button>

        <div className="mx-auto max-w-4xl">
          {/* ── Header Band ── */}
          <div className="px-6 py-5 rounded-t-xl border border-b-0 border-slate-200 bg-slate-100">
            <div className="flex justify-between items-center">
              <div className="flex gap-4 items-center">
                <img
                  alt={data.service.company_name}
                  src={`${ASSETS_URL}${data.service.logo}`}
                  className="object-contain p-1 w-14 h-14 bg-white rounded-xl shadow-sm"
                />
                <div>
                  <div className="text-lg font-bold leading-tight text-slate-900">
                    {data.service.name}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600">
                    {data.service.company_name}
                  </div>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    {t('ticket.title')}
                  </div>
                  <div className="font-mono text-2xl font-black tracking-wider text-slate-900">
                    {ticketCode}
                  </div>
                </div>
                <QRCode
                  size={256}
                  className="p-1 w-14 h-14 bg-white rounded"
                  value={data?.code ?? data?.id}
                  viewBox={`0 0 256 256`}
                />
              </div>
            </div>
          </div>

          {/* ── Date & Shift Strip ── */}
          <div className="flex border-x border-slate-200 bg-slate-50">
            <div className="flex flex-1 gap-2 items-center px-6 py-3 border-r border-dashed border-slate-300">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('ticket.service-date')}
                </div>
                <div className="font-mono text-base font-bold text-slate-800">
                  {formatDate(data.booked_date)}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center px-6 py-3">
              <Clock className="w-4 h-4 text-slate-400" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('ticket.shift')}
                </div>
                <div className="font-mono text-base font-bold text-slate-800">
                  {data.shift.from}
                </div>
              </div>
            </div>
          </div>

          {/* ── Main Content ── */}
          <div className="bg-white border-x border-slate-200">
            <div className="grid grid-cols-2 gap-0">
              {/* ── Left Column ── */}
              <div className="px-5 py-4 border-r border-dashed border-slate-300">

                {/* Variant Quantities */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {sortedVariantsByReservation(data)?.map((variant) => (
                      <div
                        key={variant.id}
                        className="flex flex-col items-center px-3 py-1 rounded border border-slate-200 bg-slate-50"
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {getPropertyValueOrReference(variant.variant, 'name')}
                        </span>
                        <div className="font-mono text-base font-bold text-slate-800">
                          {variant.amount}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dashed separator */}
                <div className="relative my-3">
                  <div className="border-t border-dashed border-slate-300" />
                  <div className="absolute -top-2 -left-5 w-4 h-4 bg-white rounded-full border-r border-slate-200" />
                  <div className="absolute -top-2 -right-5 w-4 h-4 bg-white rounded-full border-l border-dashed border-slate-300" />
                </div>

                {/* Customer Info */}
                <div className="mb-4 space-y-1.5">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('ticket.passenger')}
                    </span>
                  </div>
                  <div className="font-mono text-xs font-medium text-slate-800">
                    {data.customer_name}
                  </div>
                  {data.customer_phone && (
                    <div className="flex gap-1 items-center text-xs text-slate-600">
                      <Phone className="h-2.5 w-2.5 text-slate-400" />
                      <span className="font-mono">{data.customer_phone}</span>
                    </div>
                  )}
                  {data.customer_email && (
                    <div className="flex gap-1 items-center text-xs text-slate-600">
                      <Mail className="h-2.5 w-2.5 text-slate-400" />
                      <span className="font-mono">{data.customer_email}</span>
                    </div>
                  )}
                </div>

                {/* Payment Section (conditional) */}
                {showPayment && (
                  <>
                    <div className="relative my-2">
                      <div className="border-t border-dashed border-slate-300" />
                      <div className="absolute -top-2 -left-4 w-4 h-4 bg-white rounded-full border-r border-slate-200" />
                      <div className="absolute -top-2 -right-4 w-4 h-4 bg-white rounded-full border-l border-dashed border-slate-300" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex gap-1 items-center mb-1">
                        <CreditCard className="w-3 h-3 text-slate-400" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('reservation.payment.status')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-y-1 gap-x-4 text-xs">
                        <div>
                          <div className="font-mono font-medium text-slate-800">
                            {t(`reservation.payment.${data.payment_status}`)}
                          </div>
                        </div>
                        {data.paid_amount != null && (
                          <div>
                            <div className="text-[10px] text-slate-500">
                              {t('reservation.payment.amount')}
                            </div>
                            <div className="font-mono font-medium text-slate-800">
                              {data.paid_amount} &euro;
                            </div>
                          </div>
                        )}
                        {data.paid_at && (
                          <div>
                            <div className="text-[10px] text-slate-500">
                              {t('reservation.payment.date')}
                            </div>
                            <div className="font-mono font-medium text-slate-800">
                              {formatDateTime(data.paid_at)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── Right Column ── */}
              <div className="px-5 py-4">
                {/* Financial Summary */}
                <div className="px-3 py-3 mb-4 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('ticket.deposit')}
                      </div>
                      <div className="font-mono text-sm font-bold text-slate-800">
                        {calculateDeposit(data)}&nbsp;&euro;
                      </div>
                    </div>
                    <div className="border-x border-slate-200">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('ticket.balance')}
                      </div>
                      <div className="font-mono text-sm font-bold text-slate-800">
                        {calculateBalance(data)}&nbsp;&euro;
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('ticket.total')}
                      </div>
                      <div className="font-mono text-sm font-bold text-slate-800">
                        {calculateTotal(data)}&nbsp;&euro;
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier Info */}
                <div className="mb-4">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('ticket.supplier')}
                    </span>
                  </div>
                  <div className="font-mono text-xs leading-tight break-all text-slate-700">
                    <p>{data.service.supplier.company}</p>
                    <p>{data.service.supplier.address}</p>
                    <p>{data.service.supplier.email}</p>
                    <p>{data.service.supplier.phone_number}</p>
                  </div>
                </div>

                {/* Seller Info (conditional) */}
                {showSeller && (
                  <>
                    <div className="relative my-3">
                      <div className="border-t border-dashed border-slate-300" />
                    </div>
                    <div className="mb-4">
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('ticket.seller')}
                        </span>
                      </div>
                      <div className="font-mono text-xs leading-tight break-all text-slate-700">
                        <p>{data.seller.company}</p>
                        <p>{data.seller.address}</p>
                        <p>{data.seller.email}</p>
                        <p>{data.seller.phone_number}</p>
                      </div>
                    </div>
                  </>
                )}

                <div className="relative my-3">
                  <div className="border-t border-dashed border-slate-300" />
                </div>

                {/* Booking Agent & Datetime */}
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('ticket.seller')}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-slate-700">
                    <p>
                      {data.seller.first_name} {data.seller.last_name}
                    </p>
                    <p className="text-slate-500">
                      {formatDateTime(new Date(data.booking_date))}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer Band ── */}
          <div className="px-4 py-3 rounded-b-xl border-b border-x border-slate-200 bg-slate-50">
            {/* Service Links QR Codes */}
            {data.service.links && data.service.links.length > 0 && (
              <div className="flex gap-6 justify-center mb-2">
                {data.service.links.map((l) => (
                  <div
                    key={l.description}
                    className="flex flex-col items-center"
                  >
                    <QRCode
                      size={256}
                      className="w-14 h-14"
                      value={l.link}
                      viewBox={`0 0 256 256`}
                    />
                    <div className="mt-0.5 text-[9px] text-slate-500">
                      {l.description}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Contract / Description / Memo (small print, 2 columns) */}
            <div
              className="space-y-1 max-w-none prose prose-slate"
              style={{ fontSize: '8px', lineHeight: '1.15', columns: 2, columnGap: '1.5rem' }}
            >
              <Markdown>{data.service.description}</Markdown>
              <Markdown>{data.service.memo}</Markdown>
              <Markdown>{data.service.contract}</Markdown>
            </div>
          </div>
        </div>

        {/* Cancelled Overlay */}
        {data.status === 'cancelled' && <VoidOverlay />}
      </div>
    )
  );
}
export default Ticket;

const VoidOverlay = () => {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 flex items-center justify-center z-20!">
      <div className="transform rotate-45 text-6xl font-black text-red-500 border-[1rem] border-red-500 p-8">
        {t('ticket.voided')}
      </div>
    </div>
  );
};
